import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { UnsubscribeAttentionModal } from '@pages/Subscriptions/modals/UnsubscribeAttention.modal'
import { UnsubscribeConfirmationModal } from '@pages/Subscriptions/modals/UnsubscribeConfirmation.modal'
import { UnsubscribeOpenPositionModal } from '@pages/Subscriptions/modals/UnsubscribeOpenPosition.modal'

import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Link } from '@ui/components/Link'
import { PageSection } from '@ui/components/PageSection'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isLastBusinessDay } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'
import { isZero } from '@utils/validations'

import { SubscriptionRequestTypeEnum } from '@model/SubscriptionRequestTypeEnum'
import {
  getPendingAndProcessingSubscription,
  isSubscriptionItemActive,
  isSubscriptionItemCompleted,
} from '@model/SubscriptionTypes'
import { SubscriptionHistoryItem, SubscriptionStatus } from '@model/SubscriptionsDto'
import {
  SubscriptionRequest,
  TradingPlaformsDataProps,
  TradingPlatformsItemProps,
  TradingPlatformsPackagesProps,
} from '@model/TradingPlatformsDto'
import {
  SubscriptionRequestType,
  SubscriptionTimeInForce,
  UpdateSubscriptionRequest,
} from '@model/UpdateSubscriptionRequest'

import { useScrollToTop } from '@hooks/useScrollToTop'

import { Cart } from '../Cart'
import { SubscriptionsTemplate } from '../SubscriptionsTemplate'
import { useAvailableToWithdraw, useCartItemsData } from '../hooks/useCartItemsData'
import { PaymentDoneModal } from '../modals/PaymentDone.modal'
import { SubscriptionCQGInfoModal } from '../modals/SubscriptionCQGInfo.modal'
import { SubscriptionsTradingPlatformsTable } from './SubscriptionsTradingPlatformsTable'

export const PLATFORM_TYPE_CQG = 'cqg'
export const PLATFORM_TYPE_THIRD_PARTY = 'third-party'

export type SubscriptionDetailWithDueDate = {
  subscriptionDetailId: string
  dueDate: string
}

export const SubscriptionsTradingPlatformsPage: React.FC = () => {
  useScrollToTop()
  const clientApiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const cqgInfoModal = useModal(SubscriptionCQGInfoModal)

  const isMobile = useWindowResize()
  const paymentDoneModal = useModal(PaymentDoneModal)

  const [cQGProductsData, setCQGProductsData] = useState<TradingPlatformsItemProps[] | []>([])
  const [thirdPartyProductsData, setThirdPartyProductsData] = useState<
    TradingPlatformsItemProps[] | []
  >([])

  const [subscriptionsCQG, setCQGSubscriptions] = useState<TradingPlatformsPackagesProps[] | []>([])

  const [subscriptionsThirdParty, setThirdPartySubscriptions] = useState<
    TradingPlatformsPackagesProps[] | []
  >([])

  const {
    isExpanded,
    setIsExpanded,
    pendingUnsubscriptions,
    noneActiveSubscriptions,
    activeSubscriptions,
    forceRefreshCartItems,
    pendingUnsubscriptionsData,
    isPendingUnsubscriptionsLoading,
    isActiveSubscriptionsLoading,
    isNoneActiveSubscriptionsLoading,
    hasSubscriptions,
    upgradeOrDowngradeSubscriptionId,
  } = useCartItemsData()

  const { isTradingAccountsLoading, availableToWithdraw } = useAvailableToWithdraw()

  const getActiveSubscriptionRequest = (subscriptions: TradingPlatformsPackagesProps[]) =>
    subscriptions?.find((subscription) => subscription?.activeSubscriptionRequest)

  const isActiveAndHasActiveSubscriptionRequest = (
    subscriptions: TradingPlatformsPackagesProps[]
  ) =>
    subscriptions?.find(
      (subscription) =>
        subscription?.activeSubscriptionRequest &&
        subscription.subscriptionRequest?.state?.id === SubscriptionStatus.ACTIVE
    )

  const isCancellationRequested = (subscriptions: TradingPlatformsPackagesProps[]) =>
    subscriptions?.find(
      (subscription) =>
        (subscription.subscriptionRequest?.state?.id === SubscriptionStatus.PENDING &&
          subscription.subscriptionRequest?.type.name ===
            SubscriptionRequestTypeEnum.CancelExistingSubscription) ||
        subscription.subscriptionRequest?.state?.id === SubscriptionStatus.IN_PROCESS_CANCEL
    )

  const getPendingSubscriptionRequest = (subscriptions: TradingPlatformsPackagesProps[]) =>
    subscriptions?.find(
      (subscription) =>
        subscription.subscriptionRequest?.state?.id === SubscriptionStatus.PENDING &&
        subscription.subscriptionRequest?.type?.name ===
          SubscriptionRequestTypeEnum.AddNewSubscription &&
        subscription.subscriptionRequest?.subscriptionTimeInForce?.id !==
          SubscriptionTimeInForce.Immediate
    )

  const getPendingCancelSubscriptionRequest = (subscriptions: TradingPlatformsPackagesProps[]) =>
    subscriptions?.find(
      (subscription) =>
        subscription.subscriptionRequest?.state?.id === SubscriptionStatus.PENDING &&
        subscription.subscriptionRequest?.type?.name ===
          SubscriptionRequestTypeEnum.CancelExistingSubscription
    )

  const getDeductingFundOrInProcessSubscriptionRequest = (
    subscriptions: TradingPlatformsPackagesProps[]
  ) =>
    subscriptions?.find(
      (subscription) =>
        subscription.subscriptionRequest?.state?.id === SubscriptionStatus.DEDUCTING_FUNDS ||
        subscription.subscriptionRequest?.state?.id === SubscriptionStatus.IN_PROCESS
    )

  const refreshSubscriptionsTableData = () => {
    cqgForceRefresh()
    thirdPartyRefresh()
  }

  const handleOnUpdateSubscription = async (
    data: UpdateSubscriptionRequest,
    selectedActiveSubscriptionCartItem?: SubscriptionHistoryItem | undefined
  ) => {
    onUpdateSubscription(data, selectedActiveSubscriptionCartItem)
  }

  const onUpdateSubscription = async (
    data: UpdateSubscriptionRequest,
    selectedActiveSubscriptionCartItem?: SubscriptionHistoryItem | undefined
  ) => {
    try {
      await clientApiClient.updateSubscriptionRequest(data)
      forceRefreshCartItems()
      refreshSubscriptionsTableData()
    } catch (error: unknown) {
      if (
        error instanceof ResponseError &&
        error.response.response?.data.code === 'trading_account_has_open_positions'
      ) {
        return openPositionModal.open({
          subscriptionRequest: selectedActiveSubscriptionCartItem,
        })
      }
    } finally {
      confirmationModal.close()
      openPositionModal.close()
      attentionModal.close()
      setIsSubmitting(false)
    }
  }

  const handleOnUnsubscribeProduct = async (subscriptionRequest?: SubscriptionRequest) => {
    setIsSubmitting(true)
    if (!subscriptionRequest) {
      return
    }

    await onUpdateSubscription({
      subscriptions: [
        {
          subscriptionDetailId: subscriptionRequest?.subscriptionDetailId,
          subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
        },
      ],

      subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
    })

    openPositionModal.close()
  }

  const handleOnClickUnsubscribeWithOpenPosition = async (
    subscriptionRequest?: SubscriptionDetailWithDueDate
  ) => {
    if (!subscriptionRequest) {
      return
    }

    setIsSubmitting(true)

    await onUpdateSubscription({
      subscriptions: [
        {
          subscriptionDetailId: subscriptionRequest?.subscriptionDetailId,
          subscriptionRequestTypeId: SubscriptionRequestType.Cancel,
        },
      ],
      overrideOpenPositionsForCancel: true,
      subscriptionTimeInForceId: SubscriptionTimeInForce.BeginningOfNextMonth,
    })

    openPositionModal.close()
  }

  const handleOnClickPay = async () => {
    const noneActiveSubscriptionsMapped = noneActiveSubscriptions?.map((item) => ({
      subscriptionDetailId: item.subscriptionDetail?.id || '',
      subscriptionRequestTypeId: SubscriptionRequestType.Add,
    }))

    await clientApiClient.updateSubscriptionRequest({
      subscriptions: noneActiveSubscriptionsMapped,
      subscriptionTimeInForceId: SubscriptionTimeInForce.Immediate,
    })

    refreshSubscriptionsTableData()
    forceRefreshCartItems()
    paymentDoneModal.open()
  }

  const handleOnRemoveCartItem = async (id: string) => {
    await clientApiClient.deleteSubscriptionCartItem(id)
    refreshSubscriptionsTableData()
    forceRefreshCartItems()
  }
  const handleUnsubscribeConfirmationModal = (
    item: TradingPlatformsItemProps | TradingPlatformsPackagesProps
  ) => {
    if (isLastBusinessDay()) {
      attentionModal.open({ subscriptionRequest: item?.subscriptionRequest })
    } else {
      confirmationModal.open({ subscriptionRequest: item?.subscriptionRequest })
    }
  }

  const queryClient = useQueryClient()

  const cqgQueryKey = queryKeys.subscription.tradingPlatforms({ type: PLATFORM_TYPE_CQG, locale })
  const thirdPartyQueryKey = queryKeys.subscription.tradingPlatforms({
    type: PLATFORM_TYPE_THIRD_PARTY,
    locale,
  })

  const { data: clientSubscriptions, isLoading: isLoadingCQGData } = useQuery({
    queryKey: cqgQueryKey,
    queryFn: () => clientApiClient.getClientProductSubscriptions(PLATFORM_TYPE_CQG, locale),
  })

  const { data: thirdPartyPlatform, isLoading: isLoadingThirdPartyPlatform } = useQuery({
    queryKey: thirdPartyQueryKey,
    queryFn: () => clientApiClient.getClientProductSubscriptions(PLATFORM_TYPE_THIRD_PARTY, locale),
  })

  const cqgForceRefresh = () => {
    queryClient.removeQueries({ queryKey: cqgQueryKey })
  }

  const thirdPartyRefresh = () => {
    queryClient.removeQueries({ queryKey: thirdPartyQueryKey })
  }

  const getSubscriptionsData = (clientSubscriptions: TradingPlaformsDataProps[]) => {
    const tradingPlatformsData: TradingPlatformsItemProps[] = []
    let subscriptionsData: TradingPlatformsPackagesProps[] = []

    for (const clientSubscription of clientSubscriptions) {
      const { subscriptions, id, ...product } = clientSubscription

      const getSubscriptionStatus =
        isActiveAndHasActiveSubscriptionRequest(subscriptions) ||
        getActiveSubscriptionRequest(subscriptions) ||
        getPendingSubscriptionRequest(subscriptions) ||
        getPendingCancelSubscriptionRequest(subscriptions) ||
        isCancellationRequested(subscriptions) ||
        getDeductingFundOrInProcessSubscriptionRequest(subscriptions)

      const defaultSubscription = subscriptions?.find((subscription) => {
        const statusId = subscription?.subscriptionRequest?.state?.id

        return (
          isSubscriptionItemActive(subscription) ||
          statusId === SubscriptionStatus.PENDING ||
          statusId === SubscriptionStatus.DEDUCTING_FUNDS ||
          statusId === SubscriptionStatus.IN_PROCESS ||
          statusId === SubscriptionStatus.IN_PROCESS_CANCEL ||
          isSubscriptionItemCompleted(subscription)
        )
      })

      const subscription = getSubscriptionStatus || defaultSubscription

      tradingPlatformsData.push({
        id,
        subscriptions,
        ...product,
        type: id,
        isOpen: false,
        subscriptionRequest: getPendingAndProcessingSubscription(subscription),
      })

      const getMappedSubscriptionsData: TradingPlatformsPackagesProps[] = subscriptions?.map(
        (subscription) => {
          const detail = subscription?.details?.find(
            (detail) => detail?.amount || isZero(detail?.amount)
          )

          const bundle = subscription?.details?.find(
            ({ activeViaBundleSubscriptionRequest }) => activeViaBundleSubscriptionRequest
          )

          return {
            ...subscription,
            subscriptionRequest: getPendingAndProcessingSubscription(subscription),
            price: detail?.amount,
            productType: id,
            isActiveViaPackage: !!bundle?.activeViaBundleSubscriptionRequest,
          }
        }
      )

      subscriptionsData = subscriptionsData.concat(getMappedSubscriptionsData)
    }

    return {
      tradingPlatformsData,
      subscriptionsData,
    }
  }

  const getSubscriptionsCallback = useCallback(() => {
    const cqgSubscriptionsData = getSubscriptionsData(clientSubscriptions || [])

    setCQGSubscriptions(cqgSubscriptionsData?.subscriptionsData)
    setCQGProductsData(cqgSubscriptionsData?.tradingPlatformsData)

    const thirdPartySubscriptionsData = getSubscriptionsData(thirdPartyPlatform || [])

    setThirdPartySubscriptions(thirdPartySubscriptionsData.subscriptionsData)
    setThirdPartyProductsData(thirdPartySubscriptionsData.tradingPlatformsData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, clientSubscriptions, thirdPartyPlatform])

  useEffect(() => {
    getSubscriptionsCallback()
  }, [locale, getSubscriptionsCallback])

  const isCartEmpty = isZero(noneActiveSubscriptions?.length) && isZero(activeSubscriptions?.length)

  const isCartItemsLoading =
    isTradingAccountsLoading ||
    isNoneActiveSubscriptionsLoading ||
    isActiveSubscriptionsLoading ||
    isPendingUnsubscriptionsLoading

  const attentionModal = useModal(UnsubscribeAttentionModal, {
    onConfirm: handleOnUnsubscribeProduct,
  })

  const openPositionModal = useModal(UnsubscribeOpenPositionModal, {
    onConfirm: handleOnClickUnsubscribeWithOpenPosition,
  })

  const confirmationModal = useModal(UnsubscribeConfirmationModal, {
    onConfirm: handleOnUnsubscribeProduct,
  })

  return (
    <SubscriptionsTemplate
      subscriptionsHeader={<TradingPlatformsHeader />}
      subscriptionsTable={
        <SubscriptionsTradingPlatformsTable
          subscriptions={subscriptionsCQG}
          productsTradingPlatformsData={cQGProductsData}
          type={PLATFORM_TYPE_CQG}
          isLoading={isLoadingCQGData || isSubmitting}
          forceRefresh={cqgForceRefresh}
          forceRefreshCartItems={forceRefreshCartItems}
          onUnsubscribeConfirmationModal={handleUnsubscribeConfirmationModal}
          onSubscriptionInfoModalOpen={() => cqgInfoModal.open()}
          noneActiveSubscriptions={noneActiveSubscriptions}
        />
      }
      thirdPartySubscriptionsHeader={<ThirdPartyTradingPlatformsHeader />}
      thirdPartyTradingSubscriptionsTable={
        <SubscriptionsTradingPlatformsTable
          subscriptions={subscriptionsThirdParty}
          productsTradingPlatformsData={thirdPartyProductsData}
          type={PLATFORM_TYPE_THIRD_PARTY}
          isLoading={isLoadingThirdPartyPlatform || isSubmitting}
          forceRefresh={thirdPartyRefresh}
          forceRefreshCartItems={forceRefreshCartItems}
          onUnsubscribeConfirmationModal={handleUnsubscribeConfirmationModal}
          onSubscriptionInfoModalOpen={() => cqgInfoModal.open()}
          noneActiveSubscriptions={noneActiveSubscriptions}
        />
      }
      isCartEmpty={isMobile ? isCartEmpty : isCartEmpty && !isCartItemsLoading}
      hasSubscriptions={hasSubscriptions}
      isCartItemsLoading={isCartItemsLoading}
      sidebar={[
        <Cart
          isExpanded={isExpanded}
          setIsExpanded={setIsExpanded}
          cartItemsIsLoading={isCartItemsLoading}
          handleOnUpdateSubscription={handleOnUpdateSubscription}
          handleOnClickPay={handleOnClickPay}
          onRemoveCartItem={handleOnRemoveCartItem}
          activeSubscriptions={activeSubscriptions}
          hasSubscriptions={hasSubscriptions}
          hasPendingUnsubscriptionRequest={pendingUnsubscriptions?.itemsCount > 0}
          pendingUnsubscriptionsData={pendingUnsubscriptionsData}
          noneActiveSubscriptions={noneActiveSubscriptions}
          availableToWithdraw={availableToWithdraw}
          upgradeOrDowngradeSubscriptionId={upgradeOrDowngradeSubscriptionId}
        />,
      ]}
    />
  )
}

const TradingPlatformsHeader = () => {
  const { t } = useTranslation()
  const path = '/dashboard/subscriptions/trading-platforms/why-trading-platforms'

  return (
    <PageSection
      title={t('Subscriptions.Trading Platforms')}
      renderMessage={() => (
        <Link to={path}>{t('Subscriptions.Why and how to use a Trading Platform')}</Link>
      )}
    />
  )
}

const ThirdPartyTradingPlatformsHeader = () => {
  const { t } = useTranslation()

  return (
    <PageSection
      title={t('Subscriptions.Third Party Trading Platforms')}
      renderMessage={() => (
        <Link to='/dashboard/subscriptions/trading-platforms/why-third-party-trading-platforms'>
          {t('Subscriptions.Why and how to use API Connections')}
        </Link>
      )}
    />
  )
}
